import React from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Timer, Clock, Play, Pause, StopCircle, RefreshCw,
  CheckCircle, XCircle, AlertCircle, ChevronRight, ChevronDown,
  Minus, List, Eye, FileText, Printer, Info, MapPin,
  Building2, Globe, Layers, ChevronUp, Zap, Trash2, AlertTriangle, Calculator,
  Square, SkipForward, Lock, Calendar, QrCode, Check, X, Trash
} from 'lucide-react';
import { formatCountdown } from '../../utils/formatters';
import PenaltyCalculator from '../PenaltyCalculator';
import { LiveEventProvider } from '../live/LiveEventContext';
import EventControlBar from '../live/EventControlBar';
import ProtestSection from '../live/ProtestSection';
import RunningOrderPanel from '../live/RunningOrderPanel';
import SpectatorQrModal from '../live/SpectatorQrModal';
import LaneMatrixView from '../live/LaneMatrixView';
import FinishEventModal from '../live/FinishEventModal';
import ZoneControlBar from '../live/ZoneControlBar';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Countdown timeline configuration per federation
const COUNTDOWN_CONFIG = {
  AIDA: {
    duration_seconds: 120,
    marks: [120, 90, 60, 30, 10, 5, 4, 3, 2, 1, 0],
    labels: ['2:00', '1:30', '1:00', '0:30', '0:10', '0:05', '0:04', '0:03', '0:02', '0:01', 'OT'],
  },
  CMAS: {
    duration_seconds: 180,
    marks: [180, 150, 120, 90, 60, 30, 10, 5, 4, 3, 2, 1, 0],
    labels: ['3:00', '2:30', '2:00', '1:30', '1:00', '0:30', '0:10', '0:05', '0:04', '0:03', '0:02', '0:01', 'OT'],
  },
};

// Countdown Timeline Banner component for Live Event
function CountdownTimelineBanner({ countdown, federation, t }) {
  const config = COUNTDOWN_CONFIG[federation] || COUNTDOWN_CONFIG.CMAS;
  const { duration_seconds, marks, labels } = config;
  const clampedCountdown = Math.min(Math.max(countdown, 0), duration_seconds);
  const isActive = countdown > 0 && countdown <= duration_seconds;
  const elapsed = duration_seconds - clampedCountdown;

  return (
    <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-700/30 mt-3" data-testid="live-countdown-timeline">
      <div className="flex items-center gap-2 mb-2 text-gray-400 text-xs font-medium">
        <Timer size={12} />
        <span>{t('countdownToOfficialTop') || 'Countdown to Official TOP'}</span>
        <span className="text-xs text-gray-500">({federation} {duration_seconds / 60}min)</span>
      </div>
      <div className="relative h-9 bg-gray-800/80 rounded-lg overflow-hidden">
        {/* Progress fill — grows from left to right as time elapses */}
        {isActive && (
          <div
            className="absolute inset-y-0 left-0 rounded-lg transition-all duration-1000"
            style={{
              width: `${(elapsed / duration_seconds) * 100}%`,
              background: 'linear-gradient(to right, rgba(255,140,50,0.35), rgba(255,100,20,0.2))',
            }}
          />
        )}
        {!isActive && countdown <= 0 && (
          <div
            className="absolute inset-y-0 left-0 rounded-lg w-full"
            style={{ background: 'linear-gradient(to right, rgba(74,222,128,0.25), rgba(34,197,94,0.15))' }}
          />
        )}
        {/* Mark points */}
        <div className="absolute inset-0 flex items-center">
          {marks.map((markSec, idx) => {
            const pos = ((duration_seconds - markSec) / duration_seconds) * 100;
            const isCurrentMark = isActive && (
              idx === marks.length - 1
                ? clampedCountdown === 0
                : clampedCountdown <= markSec && (idx === marks.length - 1 || clampedCountdown > marks[idx + 1])
            );
            const isPast = clampedCountdown < markSec;
            const isOT = markSec === 0;

            return (
              <div
                key={markSec}
                className="absolute flex flex-col items-center"
                style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
              >
                <span className={`text-[10px] font-mono leading-none transition-all ${
                  isCurrentMark ? 'text-white font-bold scale-125'
                    : isPast ? 'text-gray-600' : 'text-gray-400'
                } ${isOT ? 'text-green-400 font-bold' : ''}`}>
                  {labels[idx]}
                </span>
                <div className={`w-0.5 h-2 mt-0.5 rounded-full transition-all ${
                  isCurrentMark ? 'bg-orange-400 w-1 h-3 shadow-[0_0_8px_rgba(255,140,50,0.6)]'
                    : isPast ? 'bg-gray-700' : 'bg-gray-500'
                } ${isOT ? 'bg-green-400' : ''}`} />
                {isCurrentMark && (
                  <div className="absolute -inset-2 border-2 border-orange-400/60 rounded-lg animate-pulse" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const LiveEventsTab = ({
  t, language, currentUser, competitions, eventTypes, aquaticInfrastructures = [],
  eventSessions, currentSession, currentStatus, eventStartTime, eventElapsedTime,
  countdownToNext, countdownActive, countdownPaused,
  runningOrder, showRunningOrder,
  onRefresh, onStartSession, onResetSession, onStopSession, onResetEventTimer, onToggleCountdown, onPauseCountdown,
  onCompletePassage, onCardPassage, onShowRunningOrder, onCloseRunningOrder,
  onGenerateResults, onClearOrphans, onForceStart, onSavePerformance,
  simulationMode, onSkipToNext, onResetToPending, eventSettings = {},
  onRefreshStatus
}) => {
  const [expandedDetailsId, setExpandedDetailsId] = React.useState(null);
  const [expandedRunningOrderId, setExpandedRunningOrderId] = React.useState(null);
  const [showLaneMatrix, setShowLaneMatrix] = React.useState(false);
  const [cancelConfirmId, setCancelConfirmId] = React.useState(null);

  // Performance workflow local state
  const [performancePhase, setPerformancePhase] = React.useState('countdown');
  const [stopwatchSeconds, setStopwatchSeconds] = React.useState(0);
  const [stopwatchRunning, setStopwatchRunning] = React.useState(false);
  const stopwatchRef = React.useRef(null);
  const [showPenaltyCalc, setShowPenaltyCalc] = React.useState(false);

  // OT window tracking: seconds past OT (for Start Performance button window)
  const [secondsPastOT, setSecondsPastOT] = React.useState(0);
  const [otReached, setOtReached] = React.useState(false);
  const otTimerRef = React.useRef(null);

  // Touch 25m / Realized Performance tracking during performance
  const [touchRP, setTouchRP] = React.useState(0);
  const [speedSplits, setSpeedSplits] = React.useState([]); // [{distance, elapsed_seconds, speed_ms}]

  // Finish Event modal
  const [finishModalSessionId, setFinishModalSessionId] = React.useState(null);

  // Zone selection (for 2-zone competitions, filters running order to a single zone view)
  const [selectedZone, setSelectedZone] = React.useState(null);

  // Countdown to NEXT contestant (independent of current performance phase)
  const [nextContestantCountdown, setNextContestantCountdown] = React.useState(null);
  const nextCountdownRef = React.useRef(null);

  // Post-event protest workflow
  const [protestPhase, setProtestPhase] = React.useState('none'); // 'none' | 'before_protest' | 'protest_active' | 'protest_ended'
  const [protestCountdown, setProtestCountdown] = React.useState(0);
  const protestRef = React.useRef(null);

  // Spectator QR modal
  const [qrSessionId, setQrSessionId] = React.useState(null);

  // Track if skip button was clicked for current passage
  const [skipClicked, setSkipClicked] = React.useState(false);

  // Live clock
  const [currentTime, setCurrentTime] = React.useState(new Date());
  React.useEffect(() => {
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  // Detect when countdown reaches 0 → OT reached
  React.useEffect(() => {
    if (countdownToNext !== null && countdownToNext <= 0 && performancePhase === 'countdown') {
      setPerformancePhase('ready');
      setOtReached(true);
      setSecondsPastOT(0);
    }
  }, [countdownToNext, performancePhase]);

  // Count seconds past OT for the Start Performance window
  React.useEffect(() => {
    if (otReached && performancePhase === 'ready') {
      otTimerRef.current = setInterval(() => {
        setSecondsPastOT(prev => prev + 1);
      }, 1000);
    } else {
      if (otTimerRef.current) { clearInterval(otTimerRef.current); otTimerRef.current = null; }
    }
    return () => { if (otTimerRef.current) clearInterval(otTimerRef.current); };
  }, [otReached, performancePhase]);

  // Auto-DQ after 30 seconds past OT
  React.useEffect(() => {
    if (secondsPastOT > 30 && performancePhase === 'ready' && currentStatus?.current_passage) {
      handleAutoDQ();
    }
  }, [secondsPastOT, performancePhase]);

  // When current passage changes (new contestant), reset states
  const currentPassageId = currentStatus?.current_passage?.id;
  const prevPassageIdRef = React.useRef(null);
  React.useEffect(() => {
    if (currentPassageId && currentPassageId !== prevPassageIdRef.current) {
      prevPassageIdRef.current = currentPassageId;
      const passageStatus = currentStatus?.current_passage?.status;
      if (passageStatus === 'awaiting_results') {
        setPerformancePhase('results');
        setShowPenaltyCalc(true);
      } else if (passageStatus === 'performing') {
        setPerformancePhase('performing');
      } else {
        setPerformancePhase('countdown');
      }
      setStopwatchSeconds(0);
      setStopwatchRunning(false);
      setOtReached(false);
      setSecondsPastOT(0);
      setShowPenaltyCalc(false);
      setSkipClicked(false);

      // Initialize next contestant countdown from running order
      initNextContestantCountdown();
    }
  }, [currentPassageId, currentStatus]);

  // Also sync performancePhase when countdownToNext updates and we're in countdown
  React.useEffect(() => {
    if (performancePhase === 'countdown' && countdownToNext !== null && countdownToNext <= 0) {
      setPerformancePhase('ready');
      setOtReached(true);
      setSecondsPastOT(0);
    }
  }, [countdownToNext]);

  // Initialize countdown to NEXT contestant from running order data
  const initNextContestantCountdown = () => {
    if (!runningOrder?.running_order || !currentStatus?.current_passage) return;
    const sorted = [...runningOrder.running_order].sort((a, b) => (a.passage_order || 0) - (b.passage_order || 0));
    const currentIdx = sorted.findIndex(p => p.id === currentStatus.current_passage.id);
    const nextPassage = currentIdx >= 0 ? sorted[currentIdx + 1] : null;
    if (nextPassage && nextPassage.seconds_to_official_top > 0) {
      setNextContestantCountdown(nextPassage.seconds_to_official_top);
    } else {
      setNextContestantCountdown(null);
    }
  };

  // Decrement next contestant countdown
  React.useEffect(() => {
    if (nextContestantCountdown !== null && nextContestantCountdown > 0) {
      nextCountdownRef.current = setInterval(() => {
        setNextContestantCountdown(prev => {
          if (prev <= 1) { clearInterval(nextCountdownRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (nextCountdownRef.current) clearInterval(nextCountdownRef.current); };
  }, [nextContestantCountdown]);

  // Post-event protest countdown system
  const allPassagesCompleted = currentStatus && !currentStatus.current_passage && currentStatus.session && currentStatus.session.status !== 'closed';

  // Reset protest phase when event is restarted (passages become available again)
  React.useEffect(() => {
    if (!allPassagesCompleted && protestPhase !== 'none') {
      setProtestPhase('none');
      setProtestCountdown(0);
      if (protestRef.current) { clearInterval(protestRef.current); protestRef.current = null; }
    }
  }, [allPassagesCompleted]);

  React.useEffect(() => {
    if (allPassagesCompleted && protestPhase === 'none') {
      const beforeProtestMin = (eventSettings || {}).duration_before_protest_minutes || 15;
      setProtestCountdown(beforeProtestMin * 60);
      setProtestPhase('before_protest');
    }
  }, [allPassagesCompleted, currentStatus, protestPhase, eventSettings]);

  React.useEffect(() => {
    if (protestPhase === 'before_protest' || protestPhase === 'protest_active') {
      protestRef.current = setInterval(() => {
        setProtestCountdown(prev => {
          if (prev <= 1) {
            clearInterval(protestRef.current);
            if (protestPhase === 'before_protest') {
              const protestDurMin = (eventSettings || {}).protest_time_duration_minutes || 15;
              setProtestPhase('protest_active');
              return protestDurMin * 60;
            } else {
              setProtestPhase('protest_ended');
              return 0;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (protestRef.current) clearInterval(protestRef.current); };
  }, [protestPhase]);

  // Stopwatch interval
  React.useEffect(() => {
    if (stopwatchRunning) {
      stopwatchRef.current = setInterval(() => setStopwatchSeconds(prev => prev + 0.1), 100);
    } else if (stopwatchRef.current) {
      clearInterval(stopwatchRef.current); stopwatchRef.current = null;
    }
    return () => { if (stopwatchRef.current) clearInterval(stopwatchRef.current); };
  }, [stopwatchRunning]);

  const handleStartPerformance = async () => {
    if (!currentStatus?.current_passage) return;
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${API_BASE_URL}/api/passages/${currentStatus.current_passage.id}/start-performance`);
    } catch (e) { console.log('Start performance:', e); }
    setPerformancePhase('performing');
    setStopwatchSeconds(0);
    setStopwatchRunning(true);
    setOtReached(false);
    setSecondsPastOT(0);
    setTouchRP(0);
    setSpeedSplits([]);
  };

  const handleEndPerformance = async () => {
    setStopwatchRunning(false);
    const duration = Math.floor(stopwatchSeconds);
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${API_BASE_URL}/api/passages/${currentStatus.current_passage.id}/end-performance`, { duration_seconds: duration });
    } catch (e) { console.log('End performance:', e); }
    setPerformancePhase('results');
    setShowPenaltyCalc(true);
  };

  const handleSaveAndAdvance = async (data) => {
    if (onSavePerformance) await onSavePerformance(data);
    if (currentStatus?.current_passage && onCompletePassage) await onCompletePassage(currentStatus.current_passage, 'completed');
    setPerformancePhase('countdown');
    setStopwatchSeconds(0);
    setStopwatchRunning(false);
    setShowPenaltyCalc(false);
    setOtReached(false);
    setSecondsPastOT(0);
    setTouchRP(0);
    setSpeedSplits([]);
    if (onRefreshStatus) onRefreshStatus();
  };

  const handleAutoDQ = async () => {
    if (!currentStatus?.current_passage) return;
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${API_BASE_URL}/api/passages/${currentStatus.current_passage.id}/dq`, { reason: 'Exceeded 30s after Official Top' });
    } catch (e) { console.log('Auto DQ:', e); }
    setPerformancePhase('countdown');
    setStopwatchSeconds(0);
    setStopwatchRunning(false);
    setOtReached(false);
    setSecondsPastOT(0);
    if (onRefreshStatus) onRefreshStatus();
  };

  const handleSkipToNext = async () => {
    if (onSkipToNext) await onSkipToNext();
    setSkipClicked(true);
    // Stay in countdown phase — the countdown will naturally reach OT
    setPerformancePhase('countdown');
    setStopwatchSeconds(0);
    setStopwatchRunning(false);
    setOtReached(false);
    setSecondsPastOT(0);
    setTouchRP(0);
    if (onRefreshStatus) onRefreshStatus();
  };

  const handleCloseEvent = async () => {
    if (!currentSession) return;
    if (!confirm(language === 'fr'
      ? 'Etes-vous sur de vouloir fermer cet evenement?'
      : 'Are you sure to close this event?')) return;
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${API_BASE_URL}/api/event-sessions/${currentSession}/close`);
      setProtestPhase('none');
      if (onRefresh) onRefresh();
      if (onRefreshStatus) onRefreshStatus();
    } catch (e) {
      console.error('Close event error:', e);
      alert(e.response?.data?.detail || 'Error closing event');
    }
  };

  const formatStopwatch = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const tenths = Math.floor((seconds * 10) % 10);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${tenths}`;
  };

  // Show Start Performance button only within OT window: from 10s before OT to 30s after
  const showStartButton = performancePhase === 'ready' && secondsPastOT <= 30;
  const countdownInOTWindow = performancePhase === 'countdown' && countdownToNext !== null && countdownToNext <= 10 && countdownToNext > 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': case 'scheduled':
        return <Badge className="bg-yellow-600">{t('pending') || 'Pending'}</Badge>;
      case 'preparation':
        return <Badge className="bg-orange-600">{t('inProgress') || 'Preparation'}</Badge>;
      case 'active':
        return <Badge className="bg-green-600">{t('active') || 'Active'}</Badge>;
      case 'completed':
        return <Badge className="bg-blue-600">{t('completed') || 'Completed'}</Badge>;
      case 'closed':
        return <Badge className="bg-purple-600">{t('closed') || 'Closed'}</Badge>;
      default:
        return <Badge className="bg-gray-600">{status}</Badge>;
    }
  };

  const getPassageStatusIcon = (status, cardResult) => {
    if (cardResult === 'white') return <CheckCircle className="text-green-400" size={18} />;
    if (cardResult === 'yellow') return <AlertCircle className="text-yellow-400" size={18} />;
    if (cardResult === 'red') return <XCircle className="text-red-400" size={18} />;
    switch (status) {
      case 'completed': return <CheckCircle className="text-green-400" size={18} />;
      case 'performing': return <Play className="text-yellow-400" size={18} />;
      case 'awaiting_results': return <FileText className="text-orange-400" size={18} />;
      case 'dns': return <Minus className="text-gray-400" size={18} />;
      case 'dq': return <XCircle className="text-red-400" size={18} />;
      default: return <Clock className="text-blue-400" size={18} />;
    }
  };

  const formatTimeHHMM = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return '-'; }
  };

  const formatAnnouncedPerformance = (passage) => {
    const ap = passage.announced_performance || passage.registration?.announced_performance;
    if (!ap) return '-';
    const eventCode = passage.registration?.event_type_code || runningOrder?.session?.event_type_code || '';
    if (eventCode === 'STA') {
      const totalSec = Math.round(ap);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }
    return `${ap}m`;
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleToggleRunningOrder = (session) => {
    if (expandedRunningOrderId === session.id) {
      setExpandedRunningOrderId(null); setShowLaneMatrix(false);
    } else {
      setExpandedRunningOrderId(session.id); onShowRunningOrder(session);
    }
  };

  const contextValue = {
    t, language, currentSession, currentStatus, eventElapsedTime,
    onResetSession, onStopSession, onRefresh: onRefreshStatus, runningOrder, competitions, eventTypes,
    protestPhase, protestCountdown, handleCloseEvent
  };

  return (
    <LiveEventProvider value={contextValue}>
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl md:text-3xl font-bold text-blue-200" data-testid="live-events-title">
          {t('liveEventManagement')}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {onClearOrphans && (
            <Button onClick={onClearOrphans} variant="outline" size="sm" className="border-orange-600 text-orange-300 hover:bg-orange-700/30" data-testid="clear-orphan-sessions-btn">
              <Trash2 className="mr-1.5" size={14} />
              {t('clearOrphanSessions') || 'Clear Orphan Sessions'}
            </Button>
          )}
          <Button onClick={onRefresh} className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white border-none" size="sm" data-testid="refresh-sessions-btn">
            <RefreshCw className="mr-1.5" size={14} />
            {t('refreshSessions') || 'Refresh Sessions'}
          </Button>
        </div>
      </div>

      {/* Simulation Mode Banner */}
      {simulationMode && (
        <div className="bg-orange-900/30 border border-orange-600/40 rounded-lg px-4 py-2 flex items-center gap-2" data-testid="simulation-mode-banner">
          <AlertTriangle size={16} className="text-orange-400" />
          <span className="text-orange-200 text-sm font-semibold">{t('simulationModeActive') || 'Simulation Mode Active'}</span>
          <span className="text-orange-400 text-xs">{t('simulationModeHint') || 'Use "Skip to Official Countdown" to fast-forward through passages'}</span>
        </div>
      )}

      {/* Global Event Timer */}
      {eventStartTime && (
        <Card className="bg-gradient-to-r from-indigo-900/70 to-blue-900/70 border-indigo-700/50 backdrop-blur-sm">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              {/* Left: competition info */}
              <div className="flex flex-col">
                <div className="flex items-center text-indigo-200 font-semibold">
                  <Timer className="mr-2" size={20} />
                  {t('globalEventTimer') || 'Global Event Timer'}
                </div>
                {currentStatus && (
                  <div className="mt-1">
                    <span className="text-lg font-semibold text-yellow-300">
                      {competitions.find(c => c.id === eventSessions.find(s => s.id === currentSession)?.competition_id)?.name || t('competitionsTitle')}
                    </span>
                    <span className="text-indigo-300 mx-2">&bull;</span>
                    <span className="text-indigo-200">
                      {currentStatus?.session?.name || eventSessions.find(s => s.id === currentSession)?.name || ''}
                    </span>
                  </div>
                )}
              </div>
              {/* Center: live clock + date */}
              <div className="flex flex-col items-center" data-testid="live-clock">
                <div className="flex items-center gap-2 text-indigo-300 text-xs mb-1">
                  <Calendar size={14} />
                  {currentTime.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div className="text-3xl md:text-4xl font-bold font-mono text-white" data-testid="live-clock-time">
                  {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                </div>
              </div>
              {/* Right: duration of event + timer */}
              <div className="flex flex-col items-end">
                <p className="text-indigo-300 text-xs mb-1">{t('durationOfEvent') || 'Duration of the Event'}</p>
                <div className="text-3xl md:text-4xl font-bold font-mono text-indigo-100" data-testid="event-elapsed-timer">
                  {formatCountdown(eventElapsedTime)}
                </div>
                {currentSession && (
                  <EventControlBar onOpenFinishModal={(sid) => setFinishModalSessionId(sid)} />
                )}
                {currentSession && (() => {
                  const liveSession = eventSessions.find(s => s.id === currentSession);
                  const liveCompetition = (competitions || []).find(c => c.id === liveSession?.competition_id);
                  if (!liveCompetition?.zone_config || (liveCompetition.zone_config.zone_count || 1) < 2) return null;
                  return (
                    <ZoneControlBar
                      sessionId={currentSession}
                      currentUser={currentUser}
                      competition={liveCompetition}
                      selectedZone={selectedZone}
                      onSelectZone={setSelectedZone}
                      t={t}
                    />
                  );
                })()}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="bg-indigo-800/40 p-3 rounded">
                <p className="text-indigo-300 text-xs mb-1">{t('eventStarted') || 'Event Started'}</p>
                <p className="text-white font-semibold text-base md:text-lg">
                  {new Date(eventStartTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                </p>
              </div>
              <div className="bg-indigo-800/40 p-3 rounded">
                <p className="text-indigo-300 text-xs mb-1">{t('briefingDuration') || 'Briefing'}</p>
                <p className="text-white font-semibold">{eventSettings.briefing_duration_minutes || 15} {t('minutes') || 'min'}</p>
              </div>
              <div className="bg-indigo-800/40 p-3 rounded">
                <p className="text-indigo-300 text-xs mb-1">{t('warmupDuration') || 'Warmup'}</p>
                <p className="text-white font-semibold">{eventSettings.warmup_duration_minutes || 45} {t('minutes') || 'min'}</p>
              </div>
              <div className="bg-indigo-800/40 p-3 rounded">
                <p className="text-indigo-300 text-xs mb-1">{t('passageDuration') || 'Passage'}</p>
                <p className="text-white font-semibold">{eventSettings.duration_per_passage_minutes || 6} {t('minutes') || 'min'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Live Status - Performance Workflow */}
      {currentStatus && (
        <Card className="bg-gradient-to-r from-green-900/60 to-teal-900/60 border-green-700/30 backdrop-blur-sm">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="text-green-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="flex items-center">
                <Clock className="mr-2" size={20} />
                {t('liveEvent') || 'Live Event'}: {currentStatus.session?.name}
              </div>
              {/* Countdown + Next Contestant Display */}
              <div className="flex items-center space-x-3">
                {/* Time to Next Contestant - shown during countdown phase */}
                {countdownToNext !== null && countdownToNext > 0 && performancePhase === 'countdown' && (
                  <div className="text-center">
                    <p className="text-xs text-green-300 mb-1">{t('timeToNextContestant') || 'Time to Official Top'}</p>
                    <div className={`text-2xl md:text-4xl font-bold px-4 md:px-6 py-2 md:py-3 rounded-lg ${
                      countdownToNext <= 60 ? 'bg-red-800/80 text-red-200' :
                      countdownToNext <= 180 ? 'bg-orange-800/80 text-orange-200' :
                      'bg-green-800/80 text-green-200'
                    }`} data-testid="countdown-to-next">
                      {formatCountdown(countdownToNext)}
                    </div>
                  </div>
                )}
                {/* Time to Next Contestant - shown DURING performance or results entry */}
                {(performancePhase === 'performing' || performancePhase === 'results') && nextContestantCountdown !== null && nextContestantCountdown > 0 && (
                  <div className="text-center" data-testid="next-contestant-countdown">
                    <p className="text-xs text-teal-300 mb-1">{t('timeToNextContestant') || 'Time to Next Contestant'}</p>
                    <div className="text-xl md:text-2xl font-bold px-3 py-1.5 rounded-lg bg-teal-800/60 text-teal-200 font-mono">
                      {formatCountdown(nextContestantCountdown)}
                    </div>
                  </div>
                )}
                {/* Countdown controls */}
                {performancePhase === 'countdown' && countdownToNext !== null && countdownToNext > 0 && (
                  <div className="flex flex-col gap-2">
                    <Button onClick={onToggleCountdown} className={countdownActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} size="sm">
                      {countdownActive ? <Pause size={16} className="mr-1" /> : <Play size={16} className="mr-1" />}
                      {countdownActive ? t('stop') || 'Stop' : t('start') || 'Start'}
                    </Button>
                    {countdownActive && (
                      <Button onClick={onPauseCountdown} className={countdownPaused ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'} size="sm">
                        {countdownPaused ? t('resume') || 'Resume' : t('pause') || 'Pause'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Countdown Timeline Banner */}
            {countdownToNext !== null && countdownToNext > 0 && performancePhase === 'countdown' && (() => {
              const session = eventSessions.find(s => s.id === currentSession);
              const comp = competitions.find(c => c.id === session?.competition_id);
              const fed = (comp?.federation || 'CMAS').toUpperCase();
              return <CountdownTimelineBanner countdown={countdownToNext} federation={fed} t={t} />;
            })()}
            {currentStatus.current_passage && (
              <div className="space-y-4">
                {/* Current Contestant Info */}
                <div className="bg-green-800/30 p-3 md:p-4 rounded-lg" data-testid="current-contestant-panel">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-green-200 font-semibold">{t('currentContestant') || 'Current Contestant'}</h4>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${
                        performancePhase === 'performing' ? 'bg-red-600 animate-pulse' :
                        performancePhase === 'results' ? 'bg-orange-600' :
                        performancePhase === 'ready' ? 'bg-yellow-600' :
                        'bg-blue-600'
                      }`} data-testid="performance-phase-badge">
                        {performancePhase === 'countdown' && (t('waitingForOT') || 'Waiting for OT')}
                        {performancePhase === 'ready' && (t('officialTop') || 'OFFICIAL TOP')}
                        {performancePhase === 'performing' && (t('performanceInProgress') || 'PERFORMING')}
                        {performancePhase === 'results' && (t('enterResults') || 'ENTER RESULTS')}
                      </Badge>
                      {/* Skip to Official Countdown - only in simulation mode during countdown/ready, hidden after click */}
                      {simulationMode && (performancePhase === 'countdown' || performancePhase === 'ready') && !skipClicked && (
                        <Button onClick={handleSkipToNext} className="bg-orange-600 hover:bg-orange-700 text-white" size="sm" data-testid="skip-to-official-countdown-btn">
                          <SkipForward size={14} className="mr-1" />
                          {t('skipToOfficialCountdown') || 'Skip to Official Countdown'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {/* Contestant Photo */}
                      {currentStatus.current_passage.freediver?.photo_url ? (
                        <img src={currentStatus.current_passage.freediver.photo_url} alt=""
                          className="w-12 h-12 rounded-full object-cover border-2 border-green-500/50 flex-shrink-0" data-testid="live-contestant-photo" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-700 to-teal-800 flex items-center justify-center text-white text-lg font-bold border-2 border-green-500/30 flex-shrink-0" data-testid="live-contestant-photo-placeholder">
                          {(currentStatus.current_passage.freediver_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-white text-xl font-bold" data-testid="current-freediver-name">
                          #{currentStatus.current_passage.passage_order || currentStatus.progress?.completed + 1} {currentStatus.current_passage.freediver_name}
                        </p>
                        <p className="text-green-300">
                          <span className="font-bold">{currentStatus.current_passage.nationality || ''}</span>
                          {currentStatus.current_passage.nationality && ' \u2022 '}
                          {t('announced') || 'AP'}: <span className="font-bold text-white">{(() => {
                            const liveSession = eventSessions.find(s => s.id === currentSession);
                            const isSTALive = (liveSession?.event_type_code || currentStatus?.session?.event_type_code || '') === 'STA';
                            const ap = currentStatus.current_passage.announced_performance;
                            if (isSTALive && ap) { const m = Math.floor(ap / 60); const s = Math.round(ap % 60); return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`; }
                            return ap != null ? `${ap}m` : '-';
                          })()}</span>
                          {currentStatus.current_passage.lane && currentStatus.current_passage.lane !== '-' && (
                            <> &bull; {t('lane') || 'Lane'} {currentStatus.current_passage.lane}</>
                          )}
                        </p>
                      </div>
                    </div>
                    {/* Performance Action Buttons */}
                    <div className="flex gap-2 flex-wrap items-center">
                      {/* Start Performance: visible from OT-10s to OT+30s */}
                      {(showStartButton || countdownInOTWindow) && (
                        <div className="flex items-center gap-3">
                          {secondsPastOT > 0 && (
                            <div className="text-center">
                              <p className="text-red-300 text-xs">{t('timeLimit') || 'Time limit'}</p>
                              <p className="text-red-200 font-bold font-mono text-lg">{30 - secondsPastOT}s</p>
                            </div>
                          )}
                          <Button onClick={handleStartPerformance} className="bg-green-600 hover:bg-green-700 text-white text-lg px-6 py-3 animate-pulse" data-testid="start-performance-btn">
                            <Play size={20} className="mr-2" />
                            {t('startPerformance') || 'Start Performance'}
                          </Button>
                        </div>
                      )}
                      {/* Stopwatch + Touch [poolLength]m + End Button (STA = duration only, no touch) */}
                      {performancePhase === 'performing' && (() => {
                        const liveSession = eventSessions.find(s => s.id === currentSession);
                        const isSTAEvent = (liveSession?.event_type_code || currentStatus?.session?.event_type_code || '') === 'STA';
                        const liveCompetition = (competitions || []).find(c => c.id === liveSession?.competition_id);
                        const poolLength = parseInt(liveCompetition?.pool_length) || 25;
                        return (
                        <>
                          {!isSTAEvent && (
                            <>
                              <div className="bg-cyan-900/40 border border-cyan-600/50 rounded-lg px-3 py-2 text-center min-w-[100px]" data-testid="performing-rp-display">
                                <p className="text-cyan-300 text-xs mb-0.5">RP</p>
                                <p className="text-white font-bold text-xl font-mono">{touchRP}m</p>
                              </div>
                              <Button
                                onClick={async () => {
                                  const newDist = touchRP + poolLength;
                                  const elapsed = Math.round(stopwatchSeconds * 10) / 10;
                                  const prevElapsed = speedSplits.length > 0 ? speedSplits[speedSplits.length - 1].elapsed_seconds : 0;
                                  const segmentSeconds = Math.round((elapsed - prevElapsed) * 10) / 10;
                                  const segmentSpeed = segmentSeconds > 0 ? Math.round((poolLength / segmentSeconds) * 100) / 100 : 0;
                                  const newSplit = { distance: newDist, elapsed_seconds: elapsed, segment_seconds: segmentSeconds, segment_speed_ms: segmentSpeed, segment_length: poolLength };
                                  const newSplits = [...speedSplits, newSplit];
                                  setSpeedSplits(newSplits);
                                  setTouchRP(newDist);
                                  try {
                                    const axios = (await import('axios')).default;
                                    await axios.patch(`${API_BASE_URL}/api/event-sessions/${currentSession}/live-performance`, {
                                      speed_splits: newSplits, distance_covered: newDist, performance_time_seconds: elapsed
                                    });
                                  } catch (e) { console.error('Live update failed:', e); }
                                }}
                                className="bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white h-[110px] min-w-[180px] px-8 text-4xl font-extrabold shadow-lg shadow-orange-900/50 ring-2 ring-orange-400/50 active:scale-95 transition-transform"
                                data-testid="touch-pool-btn"
                              >
                                +{poolLength}m
                              </Button>
                              {touchRP > 0 && stopwatchSeconds > 0 && (
                                <div className="bg-blue-900/40 border border-blue-600/40 rounded-lg px-3 py-2 text-center min-w-[80px]" data-testid="speed-display">
                                  <p className="text-blue-300 text-xs mb-0.5">Speed</p>
                                  <p className="text-white font-bold text-lg font-mono">{(touchRP / stopwatchSeconds).toFixed(2)} <span className="text-xs text-gray-400">m/s</span></p>
                                </div>
                              )}
                            </>
                          )}
                          <div className="bg-red-900/60 border border-red-600 rounded-lg px-4 py-2 text-center" data-testid="performance-stopwatch">
                            <p className="text-red-300 text-xs mb-1">{t('performanceDuration') || 'Duration'}</p>
                            <p className="text-white font-bold text-2xl font-mono">{formatStopwatch(stopwatchSeconds)}</p>
                          </div>
                          <Button onClick={handleEndPerformance} className="bg-red-600 hover:bg-red-700 text-white text-lg h-[52px] px-6" data-testid="end-performance-btn">
                            <Square size={20} className="mr-2" />
                            {t('endPerformance') || 'End Performance'}
                          </Button>
                        </>
                        );
                      })()}
                      {/* Results indicator */}
                      {performancePhase === 'results' && (
                        <div className="flex items-center gap-2 text-orange-300">
                          <FileText size={18} />
                          <span className="text-sm font-semibold">{t('enterResultsBelow') || 'Enter results below'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Speed Splits Table - During Performance */}
                  {performancePhase === 'performing' && speedSplits.length > 0 && (() => {
                    const liveSession = eventSessions.find(s => s.id === currentSession);
                    const liveCompetition = (competitions || []).find(c => c.id === liveSession?.competition_id);
                    const poolLength = parseInt(liveCompetition?.pool_length) || 25;
                    return (
                    <div className="mt-3 bg-indigo-900/30 border border-indigo-600/30 rounded-lg p-3" data-testid="speed-splits-panel">
                      <p className="text-indigo-300 text-xs font-semibold mb-2">{t('speedSplits') || `${poolLength}m Speed Splits`}</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {speedSplits.map((split, i) => (
                          <div key={i} className="bg-indigo-800/40 rounded px-2 py-1.5 text-center" data-testid={`speed-split-${i}`}>
                            <p className="text-indigo-200 text-xs font-bold">{split.distance}m</p>
                            <p className="text-white font-mono text-sm">{split.segment_seconds.toFixed(1)}s</p>
                            <p className="text-cyan-300 font-mono text-xs">{split.segment_speed_ms.toFixed(2)} m/s</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    );
                  })()}

                  {/* Next Contestant Preview - PROMINENT */}
                  {currentStatus.next_passage_name && (
                    <div className="mt-4 bg-teal-900/40 border border-teal-600/40 rounded-lg p-3" data-testid="next-contestant-preview">
                      <div className="flex items-center gap-3 flex-wrap">
                        <ChevronRight size={20} className="text-teal-400 shrink-0" />
                        <span className="text-teal-200 text-base font-semibold">{t('nextContestant') || 'Next contestant'}:</span>
                        <span className="text-white text-lg font-bold">
                          {currentStatus.next_passage?.freediver
                            ? `${currentStatus.next_passage.freediver.first_name} ${currentStatus.next_passage.freediver.last_name}`
                            : currentStatus.next_passage_name}
                        </span>
                        {currentStatus.next_passage?.announced_performance != null && (
                          <Badge className="bg-teal-700/60 text-teal-200 text-sm">
                            AP: {(() => {
                              const liveSession = eventSessions.find(s => s.id === currentSession);
                              const isSTALive = (liveSession?.event_type_code || currentStatus?.session?.event_type_code || '') === 'STA';
                              const ap = currentStatus.next_passage.announced_performance;
                              if (isSTALive && ap) { const m = Math.floor(ap / 60); const s = Math.round(ap % 60); return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`; }
                              return ap != null ? `${ap}m` : '-';
                            })()}
                          </Badge>
                        )}
                        {/* Zone times for next contestant */}
                        {currentStatus.next_passage?.warmup_time && (
                          <div className="flex items-center gap-2 text-xs font-mono ml-auto">
                            <span className="text-blue-300" title={t('warmup') || 'Warm-up'}>{t('warmup') || 'W-up'}: <span className="text-blue-100">{formatTimeHHMM(currentStatus.next_passage.warmup_time)}</span></span>
                            <span className="text-yellow-300" title={t('transit') || 'Transit'}>{t('transit') || 'Trans.'}: <span className="text-yellow-100">{formatTimeHHMM(currentStatus.next_passage.transition_time)}</span></span>
                            <span className="text-orange-300" title={t('official') || 'Official'}>{t('official') || 'Off.'}: <span className="text-orange-100">{formatTimeHHMM(currentStatus.next_passage.countdown_time)}</span></span>
                          </div>
                        )}
                        {nextContestantCountdown !== null && nextContestantCountdown > 0 && (
                          <span className="ml-auto text-teal-300 font-mono font-bold text-base">
                            OT: {formatCountdown(nextContestantCountdown)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Results Entry Panel */}
                {performancePhase === 'results' && (() => {
                  const session = eventSessions.find(s => s.id === currentSession);
                  const competition = competitions.find(c => c.id === session?.competition_id);
                  return (
                    <div className="bg-orange-900/20 border border-orange-600/30 rounded-lg p-4" data-testid="results-entry-panel">
                      <h4 className="text-orange-200 font-semibold mb-3 flex items-center gap-2">
                        <Calculator size={18} />
                        {t('performanceResults') || 'Performance Results'}
                        {stopwatchSeconds > 0 && (
                          <span className="text-sm font-mono text-orange-300 ml-2">({t('duration') || 'Duration'}: {formatStopwatch(stopwatchSeconds)})</span>
                        )}
                      </h4>
                      <PenaltyCalculator t={t} language={language}
                        federation={competition?.federation || 'AIDA'}
                        eventCode={session?.event_type_code || 'DYN'}
                        announced={currentStatus.current_passage.announced_performance || 0}
                        realized={session?.event_type_code === 'STA' ? Math.floor(stopwatchSeconds) : (touchRP > 0 ? touchRP : (currentStatus.current_passage.distance_covered || 0))}
                        passageId={currentStatus.current_passage.id}
                        onSave={(data) => handleSaveAndAdvance({ ...data, speed_splits: speedSplits })}
                        performanceDuration={Math.floor(stopwatchSeconds)}
                      />
                    </div>
                  );
                })()}

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-green-800/30 p-3 rounded text-center">
                    <p className="text-green-300 text-xs">{t('totalPassages') || 'Total'}</p>
                    <p className="text-white font-bold text-xl">{currentStatus.total_passages || 0}</p>
                  </div>
                  <div className="bg-green-800/30 p-3 rounded text-center">
                    <p className="text-green-300 text-xs">{t('completed') || 'Done'}</p>
                    <p className="text-white font-bold text-xl">{currentStatus.completed_passages || 0}</p>
                  </div>
                  <div className="bg-green-800/30 p-3 rounded text-center">
                    <p className="text-green-300 text-xs">{t('remaining') || 'Remaining'}</p>
                    <p className="text-white font-bold text-xl">{(currentStatus.total_passages || 0) - (currentStatus.completed_passages || 0)}</p>
                  </div>
                  <div className="bg-green-800/30 p-3 rounded text-center">
                    <p className="text-green-300 text-xs">{t('currentLane') || 'Lane'}</p>
                    <p className="text-white font-bold text-xl">{currentStatus.current_passage?.lane || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* All Passages Completed - Protest Workflow */}
            {!currentStatus.current_passage && currentStatus.session && (
              <ProtestSection />
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Sessions List */}
      {(() => {
        const liveSessions = eventSessions.filter(s => s.status !== 'closed' && s.status !== 'completed');
        return (
      <Card className="bg-gradient-to-br from-blue-800/40 to-purple-800/40 border-blue-700/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-blue-200 flex items-center">
            <List className="mr-2" size={20} />
            {t('eventSessions') || 'Event Sessions'}
            {liveSessions.length > 0 && <Badge className="ml-2 bg-blue-700/50 text-blue-200">{liveSessions.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {liveSessions.length === 0 ? (
            <div className="text-center py-8">
              <Timer size={48} className="mx-auto text-blue-400 mb-4" />
              <p className="text-blue-300">{t('noEventSessions') || 'No event sessions found'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {liveSessions.map((session) => {
                const competition = competitions.find(c => c.id === session.competition_id);
                const isDetailsExpanded = expandedDetailsId === session.id;
                const isRunningOrderExpanded = expandedRunningOrderId === session.id;
                const isActive = currentSession === session.id;
                const canForceStart = session.status === 'scheduled' || session.status === 'pending' || session.status === 'preparation';
                const matchedInfra = aquaticInfrastructures.find(i => i.pool_type === competition?.pool_type && Number(i.pool_length) === Number(competition?.pool_length));

                return (
                  <div key={session.id} className="rounded-lg overflow-hidden" data-testid={`event-session-${session.id}`}>
                    <div className={`p-3 md:p-4 border transition-colors ${
                      isActive ? 'bg-green-900/40 border-green-600' : 'bg-blue-800/30 border-blue-700/30 hover:bg-blue-700/30'
                    } ${(isDetailsExpanded || isRunningOrderExpanded) ? 'rounded-t-lg border-b-0' : 'rounded-lg'}`}>
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div>
                            <h4 className="text-blue-200 font-semibold text-sm md:text-base">{session.name}</h4>
                            <p className="text-blue-400 text-xs md:text-sm">{competition?.name}</p>
                          </div>
                          {getStatusBadge(session.status)}
                          {isActive && (
                            <Badge className="bg-green-600 animate-pulse" data-testid={`live-badge-${session.id}`}>
                              <Play size={12} className="mr-1" /> LIVE
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {canForceStart && onForceStart && (
                            <Button onClick={() => onForceStart(session.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`force-start-${session.id}`}>
                              <Zap size={14} className="mr-1" />
                              {t('forceStartNow') || 'Force Start'}
                            </Button>
                          )}
                          <Button onClick={() => setQrSessionId(session.id)} size="sm" variant="outline" className="border-cyan-600 text-cyan-300 hover:bg-cyan-700/30" data-testid={`spectator-link-${session.id}`}>
                            <QrCode size={14} className="mr-1" />
                            <span className="hidden sm:inline">Spectator</span>
                          </Button>
                          {simulationMode && onResetToPending && (session.status === 'active' || session.status === 'preparation' || session.status === 'stopped') && (
                            <Button onClick={() => onResetToPending(session.id)} size="sm" variant="outline" className="border-orange-500 text-orange-300 hover:bg-orange-700/30" data-testid={`reset-to-pending-${session.id}`}>
                              <RefreshCw size={14} className="mr-1" />
                              {t('resetActiveSession') || 'Reset Active Session'}
                            </Button>
                          )}
                          <Button onClick={() => setExpandedDetailsId(isDetailsExpanded ? null : session.id)} size="sm" variant="outline"
                            className={`border-teal-600 hover:bg-teal-700/30 ${isDetailsExpanded ? 'text-teal-300 bg-teal-900/30' : 'text-teal-400'}`} data-testid={`details-toggle-${session.id}`}>
                            <Info size={14} className="mr-1" />
                            {isDetailsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </Button>
                          <Button onClick={() => handleToggleRunningOrder(session)} size="sm" variant="outline"
                            className={`border-blue-500 hover:bg-blue-700/50 ${isRunningOrderExpanded ? 'text-blue-200 bg-blue-800/40' : 'text-blue-300'}`} data-testid={`running-order-toggle-${session.id}`}>
                            <Eye size={14} className="mr-1" />
                            {t('runningOrder') || 'Running Order'}
                            {isRunningOrderExpanded ? <ChevronUp size={14} className="ml-1" /> : <ChevronDown size={14} className="ml-1" />}
                          </Button>
                          {(session.status === 'completed' || session.status === 'closed') && (
                            <Button onClick={() => onGenerateResults(session.id)} size="sm" variant="outline" className="border-teal-500 text-teal-300 hover:bg-teal-700/50" data-testid={`results-${session.id}`}>
                              <FileText size={14} className="mr-1" />
                              {t('results') || 'Results'}
                            </Button>
                          )}
                          {currentUser?.username === 'ACM-Admin' && (
                            cancelConfirmId === session.id ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API_BASE_URL}/api/event-sessions/${session.id}`);
                                      setCancelConfirmId(null);
                                      onRefresh();
                                    } catch (err) { console.error('Delete error:', err); }
                                  }}
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  data-testid={`confirm-cancel-event-${session.id}`}
                                >
                                  <Check size={14} className="mr-1" />
                                  {t('confirm') || 'Confirm'}
                                </Button>
                                <Button
                                  onClick={() => setCancelConfirmId(null)}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-600 text-blue-300 hover:bg-blue-700"
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                onClick={() => setCancelConfirmId(session.id)}
                                size="sm"
                                variant="outline"
                                className="border-red-600 text-red-300 hover:bg-red-700/30"
                                data-testid={`cancel-event-${session.id}`}
                              >
                                <Trash size={14} className="mr-1" />
                                {t('cancelEvent') || 'Cancel Event'}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Competition Details Panel */}
                    {isDetailsExpanded && competition && (
                      <div className="bg-blue-950/60 border border-blue-700/30 border-t-0 p-4 space-y-3" data-testid={`competition-details-${session.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {competition.logo_url && <img src={competition.logo_url} alt={competition.name} className="w-10 h-10 object-contain rounded bg-white/10 p-1" />}
                            <div>
                              <h3 className="text-teal-300 font-bold text-base md:text-lg">{competition.name}</h3>
                              {competition.federation && <p className="text-blue-400 text-sm">{competition.federation}</p>}
                            </div>
                          </div>
                          {matchedInfra && (
                            <div className="flex items-center gap-1 text-xs text-teal-400 bg-teal-900/30 px-2 py-1 rounded-full border border-teal-700/40">
                              <Building2 size={12} /> {matchedInfra.short_name}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <h4 className="text-blue-300 text-xs font-semibold uppercase tracking-wider">{t('competition') || 'Competition'}</h4>
                            {competition.location && (
                              <div className="flex items-center gap-2 text-sm text-blue-200"><MapPin size={13} className="text-teal-400 shrink-0" />{competition.location}</div>
                            )}
                            {(competition.start_date || competition.end_date) && (
                              <div className="flex items-center gap-2 text-sm text-blue-200">
                                <Timer size={13} className="text-teal-400 shrink-0" />
                                {competition.start_date && new Date(competition.start_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {competition.start_date && competition.end_date && ' -> '}
                                {competition.end_date && new Date(competition.end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <h4 className="text-blue-300 text-xs font-semibold uppercase tracking-wider">{t('poolDetails') || 'Pool Details'}</h4>
                            {competition.pool_type && (
                              <div className="flex items-center gap-2 text-sm text-blue-200">
                                <Layers size={13} className="text-teal-400 shrink-0" />
                                {competition.pool_type}{competition.pool_length ? ` - ${competition.pool_length}m` : ''}{competition.lanes ? ` - ${competition.lanes} ${t('lanes') || 'lanes'}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                        {matchedInfra && (
                          <div className="bg-teal-900/20 border border-teal-700/30 rounded-lg p-3 space-y-2">
                            <h4 className="text-teal-300 text-xs font-semibold uppercase flex items-center gap-1"><Building2 size={12} />{matchedInfra.long_name || matchedInfra.short_name}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-200">
                              {(matchedInfra.address_street || matchedInfra.city) && (
                                <div className="flex items-start gap-2"><MapPin size={13} className="text-teal-400 shrink-0 mt-0.5" /><span>{[matchedInfra.address_street, matchedInfra.city, matchedInfra.province, matchedInfra.postal_code, matchedInfra.country].filter(Boolean).join(', ')}</span></div>
                              )}
                              {matchedInfra.website_url && (
                                <div className="flex items-center gap-2"><Globe size={13} className="text-teal-400 shrink-0" /><a href={matchedInfra.website_url} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 underline truncate">{matchedInfra.website_url.replace(/^https?:\/\//, '')}</a></div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Running Order Table */}
                    {isRunningOrderExpanded && (
                      <div className="bg-blue-950/60 border border-blue-700/30 border-t-0 rounded-b-lg overflow-hidden" data-testid={`running-order-panel-${session.id}`}>
                        <div className="flex items-center justify-between px-4 py-2 bg-blue-900/40 border-b border-blue-700/30">
                          <div className="flex items-center gap-2">
                            <span className="text-blue-200 font-semibold text-sm">{t('runningOrder') || 'Running Order'} - {session.name}</span>
                            <span className="text-blue-400 text-xs">({runningOrder?.total_participants || 0} {t('participants') || 'participants'})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const roComp = (competitions || []).find(c => c.id === runningOrder?.session?.competition_id);
                              const lc = parseInt(roComp?.lanes) || 0;
                              return lc > 0 && (
                                <Button onClick={() => setShowLaneMatrix(v => !v)} variant="outline" size="sm"
                                  className={`border-teal-600 text-xs ${showLaneMatrix ? 'bg-teal-700/40 text-teal-200' : 'text-teal-400'}`} data-testid="lane-matrix-toggle">
                                  <Layers size={12} className="mr-1" />{showLaneMatrix ? (t('tableView') || 'Table') : (t('laneMatrix') || 'Lanes')}
                                </Button>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="max-h-[50vh] overflow-y-auto">
                          {runningOrder?.running_order?.length > 0 ? (
                            showLaneMatrix ? (
                              <LaneMatrixView competitions={competitions} runningOrder={runningOrder} formatAnnouncedPerformance={formatAnnouncedPerformance} formatTimeHHMM={formatTimeHHMM} t={t} />
                            ) : (
                              <RunningOrderPanel sessionId={session.id} sessionRunningOrder={runningOrder} zoneFilter={session.id === currentSession ? selectedZone : null} />
                            )
                          ) : (
                            <div className="text-center py-6 text-blue-400 text-sm">{t('noRunningOrderAvailable') || 'No running order available'}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
        );
      })()}

    {/* Spectator QR Code Modal */}
    {qrSessionId && (
      <SpectatorQrModal qrSessionId={qrSessionId} onClose={() => setQrSessionId(null)} />
    )}
    {finishModalSessionId && (() => {
      const sess = eventSessions.find(s => s.id === finishModalSessionId);
      const comp = (competitions || []).find(c => c.id === sess?.competition_id);
      const fed = comp?.federation || sess?.federation || 'AIDA';
      return (
        <FinishEventModal
          open={true}
          onClose={() => setFinishModalSessionId(null)}
          sessionId={finishModalSessionId}
          federation={fed}
          eventTypeCode={sess?.event_type_code}
          t={t}
          onFinished={(res) => {
            if (typeof window !== 'undefined' && window.alert) {
              window.alert((t && t('eventFinished')) || `Event finished. ${res.saved_career_performances || 0} performance(s) saved to career.`);
            }
            if (onRefreshStatus) onRefreshStatus();
            if (onRefresh) onRefresh();
          }}
        />
      );
    })()}
    </div>
    </LiveEventProvider>
  );
};

export default LiveEventsTab;
